import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff, RefreshCw, Copy, Check } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card } from "../components/ui/Card";
import { useAuth } from "../lib/auth";

function generatePassword(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  const part = (len: number) =>
    Array.from({ length: len }, () => chars.charAt(Math.floor(Math.random() * chars.length))).join("");
  return `${part(3)}-${part(3)}-${part(2)}`;
}

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register } = useAuth();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState(generatePassword());
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const packageId = searchParams.get("package") || undefined;

  const regenerate = () => {
    setPassword(generatePassword());
    setCopied(false);
  };

  const copyPassword = () => {
    navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({ email, password, firstName, lastName });
      navigate(packageId ? `/onboarding?package=${packageId}` : "/onboarding");
    } catch (err: any) {
      setError(err.response?.data?.message || "Registratie mislukt");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-gradient flex items-center justify-center px-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-40 w-[500px] h-[500px] rounded-full bg-indigo-300/30 blur-3xl animate-liquid-sway" />
        <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-brand-300/30 blur-3xl animate-liquid-sway" style={{ animationDelay: "-3s" }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        <Card className="p-8 surface-card">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4 shadow-lg shadow-brand-500/25">
              AB
            </div>
            <h1 className="text-2xl font-bold">Maak je AIBoek-account</h1>
            <p className="text-slate-500 text-sm mt-1">Voor je bedrijf of zzp-administratie</p>
          </div>

          {error && (
            <div className="mb-5 rounded-xl bg-red-50 text-red-700 px-4 py-3 text-sm border border-red-100">{error}</div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Voornaam"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
              <Input
                label="Achternaam"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
            <Input
              label="Zakelijke e-mailadres"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1.5">
                Automatisch gegenereerd wachtwoord
              </label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    readOnly
                    className="input-field w-full pr-24 font-mono tracking-widest"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-14 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                  >
                    {showPassword ? <EyeOff size={27} /> : <Eye size={27} />}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={copyPassword}
                  className="p-2.5 rounded-xl border border-slate-200/70 bg-white/60 hover:bg-white text-slate-600 transition-colors shadow-sm"
                  title="Kopieer wachtwoord"
                >
                  {copied ? <Check size={27} className="text-emerald-500" /> : <Copy size={27} />}
                </button>
                <button
                  type="button"
                  onClick={regenerate}
                  className="p-2.5 rounded-xl border border-slate-200/70 bg-white/60 hover:bg-white text-slate-600 transition-colors shadow-sm"
                  title="Nieuw wachtwoord"
                >
                  <RefreshCw size={27} />
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-1.5">
                Bewaar dit wachtwoord veilig. Je kunt het later wijzigen in de instellingen.
              </p>
            </div>

            <Button type="submit" className="w-full mt-2" loading={loading}>
              Account aanmaken
            </Button>
          </form>

          <p className="text-center text-sm text-slate-500 mt-6">
            Al een account?{" "}
            <Link to="/login" className="text-brand-600 font-bold hover:underline">
              Inloggen
            </Link>
          </p>
        </Card>
      </motion.div>
    </div>
  );
}
